/* VITALS — today's glucose curve, today's blood pressure, a fortnight of
   figures, and the form that writes a reading.

   Read-only where it reads, and deliberately arithmetic-free about it: every
   figure on this screen that is a *derived* number — the 14-day means, the
   time-in-range percentage, the 70–180 band itself — is computed by the server
   and served by `GET /vitals/summary`. The SPA draws the band it is GIVEN
   (`range_mgdl`) and renders `time_in_range_pct` verbatim; it does not know
   the number 70, and it never re-derives a percentage from the rows, or the
   app and the coach would quote different figures for the same fortnight.

   What this screen *does* compute is geometry: pixel coordinates for the curve,
   the band and the meal markers. Those are a property of the drawing, not of
   the data. Anything else that has to happen client-side — grouping a BP triple
   by its `reading_group`, rendering it as one line — is an exported pure
   function pinned by `Vitals.test.ts`, on the pattern of `Plan.tsx`.

   The form follows LOG's one rule to the letter: never lose typed input. Form
   state lives in its own `useState`s, the refetch effect never keys off it,
   the fields are cleared only after `postVitals` has resolved, a validation
   failure says so and keeps the values, and the in-flight guard is a ref that
   refuses a duplicate request without ever disabling an input.

   No charting library, for the same reason TRENDS has none: the plot is forty
   lines of SVG and every stroke is specified. */
import { useCallback, useEffect, useRef, useState } from 'react'
import type { KeyboardEvent } from 'react'
import { Alert, Button, Metric, Window } from '../ds'
import {
  errorMessage,
  getNutrition,
  getVitalDevices,
  getVitals,
  getVitalsSummary,
  postVitalDevice,
  postVitals,
  type IsoDateTime,
  type Meal,
  type NutritionEntry,
  type VitalDevice,
  type VitalIn,
  type VitalMetricSummary,
  type VitalRow,
  type VitalsSummaryResponse,
} from '../api'
import { useShellSync } from '../app/syncOnLoad'
import { headingDate } from './format'
import '../styles/vitals.css'

/* ── Constants ───────────────────────────────────────────────────────────── */

/** The window the summary strip is named for. Also the `days` sent to /vitals/summary. */
const SUMMARY_DAYS = 14

/** Minutes in the local day the x-axis spans. */
const DAY_MINUTES = 24 * 60

/* Chart geometry, the same frame as TRENDS' WEIGHT.90D: a 720×300 viewBox
   scaled to the window body by CSS, inset on the left for a 3-digit mono
   gridline label and at the bottom for the five hour labels. */
export const W = 720
export const H = 300
export const PAD_L = 44
export const PAD_R = 14
export const PAD_T = 18
export const PAD_B = 26

/** Gridline spacing, in mg/dL. */
const GRID_STEP = 50

/** Ceiling on gridline count — one absurd row must not become thousands of `<line>`s. */
const MAX_GRIDLINES = 40

/** Headroom above the highest reading (or band edge) and below the lowest, in
 *  mg/dL. Also why the y-domain can never be zero-height. */
const Y_PAD = 10

/** The hour labels along the bottom of the plot. */
const HOUR_LABELS = [0, 6, 12, 18, 24] as const

/**
 * Where a meal's marker sits, in minutes after local midnight.
 *
 * Nutrition rows carry a DATE, not a time — `/nutrition` has no clock to give —
 * so the markers cannot say when a meal was eaten, only that it was. These are
 * nominal slots, and the screen says so beside the chart. Geometry, not data.
 */
export const MEAL_MARKER_SLOTS: Record<Exclude<Meal, 'daily_total'>, number> = {
  breakfast: 8 * 60,
  lunch: 12 * 60 + 30,
  snack: 16 * 60,
  dinner: 19 * 60,
}

/** Confirmation flash lifetime, as LOG's weight flash. */
const SAVED_FLASH_MS = 2800

/* ── Loading ─────────────────────────────────────────────────────────────── */

type Load<T> =
  | { status: 'loading' }
  | { status: 'ready'; data: T }
  | { status: 'error'; message: string }

function isAbort(error: unknown): boolean {
  return error instanceof DOMException && error.name === 'AbortError'
}

/* ── Dates ───────────────────────────────────────────────────────────────── */

/** Local midnight of the day `now` falls on — the left edge of the chart and
 *  the `from` of today's read. The browser's zone, deliberately: the curve is
 *  "the day you are living", and a reading's `observed_at` is an instant, so
 *  no server day boundary is being second-guessed. */
export function localDayStart(now: Date): Date {
  return new Date(now.getFullYear(), now.getMonth(), now.getDate())
}

function twoDigit(n: number): string {
  return String(n).padStart(2, '0')
}

/** `YYYY-MM-DDTHH:mm` in local time — what `<input type="datetime-local">` holds. */
export function datetimeLocalValue(d: Date): string {
  return (
    `${d.getFullYear()}-${twoDigit(d.getMonth() + 1)}-${twoDigit(d.getDate())}` +
    `T${twoDigit(d.getHours())}:${twoDigit(d.getMinutes())}`
  )
}

/** Local `HH:MM` of an ISO instant. `new Date(iso)` is right here, unlike for
 *  the bare dates elsewhere: `observed_at` carries its offset. */
export function clockTime(iso: IsoDateTime): string {
  const d = new Date(iso)
  return `${twoDigit(d.getHours())}:${twoDigit(d.getMinutes())}`
}

/** Minutes after `dayStart` an instant fell — may run past the day, or before it. */
function minutesInto(iso: IsoDateTime, dayStart: Date): number {
  return (new Date(iso).getTime() - dayStart.getTime()) / 60_000
}

function round1(value: number): number {
  return Math.round(value * 10) / 10
}

/* ── The chart ───────────────────────────────────────────────────────────── */

/** The x pixel for a clock time, clamped to the plot: the axis is the local
 *  day and nothing else, so a reading from just before midnight sits on the
 *  left edge rather than off the canvas. */
export function xAtMinutes(minutes: number): number {
  const t = Math.min(Math.max(minutes, 0), DAY_MINUTES) / DAY_MINUTES
  return round1(PAD_L + t * (W - PAD_L - PAD_R))
}

interface Gridline {
  y: number
  labelY: number
  label: string
}

export interface GlucoseCurve {
  gridlines: Gridline[]
  dots: { x: number; y: number }[]
  path: string
  /** The target band, as a rect. Null when no range was served. */
  band: { y: number; height: number } | null
  /** The most recent reading, for the accessible name. */
  last: VitalRow
}

/**
 * Turn today's glucose rows into pixels.
 *
 * The x-axis is *clock* position across the local day, not index-within-the-
 * series: a CGM gap leaves a gap. The y-domain covers both the readings and
 * the band, so a 320 stays on the plot and the band is always drawn whole.
 * `range` is the server's `range_mgdl`, passed straight through — the band is
 * drawn where it says and nowhere else.
 */
export function buildGlucoseCurve(
  readings: readonly VitalRow[],
  range: readonly [number, number] | null,
  dayStart: Date,
): GlucoseCurve | null {
  if (readings.length === 0) return null

  // Newest first on the wire; a path is drawn oldest first.
  const ordered = [...readings].sort(
    (a, b) => new Date(a.observed_at).getTime() - new Date(b.observed_at).getTime(),
  )

  const values = ordered.map((r) => r.value)
  const bounds = range === null ? values : [range[0], range[1], ...values]
  const lo = Math.min(...bounds) - Y_PAD
  const hi = Math.max(...bounds) + Y_PAD
  const y = (value: number): number =>
    round1(PAD_T + ((hi - value) / (hi - lo)) * (H - PAD_T - PAD_B))

  const gridlines: Gridline[] = []
  for (
    let line = Math.ceil(lo / GRID_STEP) * GRID_STEP;
    line <= hi && gridlines.length < MAX_GRIDLINES;
    line += GRID_STEP
  ) {
    const at = Math.round(y(line))
    gridlines.push({ y: at, labelY: at + 4, label: String(Math.round(line)) })
  }

  const dots = ordered.map((r) => ({ x: xAtMinutes(minutesInto(r.observed_at, dayStart)), y: y(r.value) }))

  // A lone reading gets a repeated point so the round linecap renders a dot —
  // the same trick as TRENDS' rolling path.
  const steps = dots.map((d, i) => `${i ? 'L' : 'M'}${d.x} ${d.y}`)
  const path = steps.length === 1 ? `${steps[0]} L${dots[0].x} ${dots[0].y}` : steps.join(' ')

  const band =
    range === null ? null : { y: y(range[1]), height: round1(y(range[0]) - y(range[1])) }

  return { gridlines, dots, path, band, last: ordered[ordered.length - 1] }
}

export interface MealMarker {
  meal: Exclude<Meal, 'daily_total'>
  minutes: number
  x: number
  label: string
}

/** One marker per meal logged today, at its nominal slot, in clock order.
 *  `daily_total` is a day's account, not a meal, and gets no tick. */
export function mealMarkers(entries: readonly NutritionEntry[]): MealMarker[] {
  const present = new Set<Exclude<Meal, 'daily_total'>>()
  for (const entry of entries) {
    if (entry.meal !== 'daily_total') present.add(entry.meal)
  }
  return (Object.keys(MEAL_MARKER_SLOTS) as Exclude<Meal, 'daily_total'>[])
    .filter((meal) => present.has(meal))
    .map((meal) => {
      const minutes = MEAL_MARKER_SLOTS[meal]
      return { meal, minutes, x: xAtMinutes(minutes), label: meal.toUpperCase() }
    })
    .sort((a, b) => a.minutes - b.minutes)
}

/* ── Blood pressure ──────────────────────────────────────────────────────── */

export interface BpGroup {
  key: string
  observed_at: IsoDateTime
  systolic: number | null
  diastolic: number | null
  pulse: number | null
  /** `device_label`, falling back to the slug, falling back to nothing. */
  device: string | null
}

const BP_METRICS = new Set<VitalRow['metric']>(['systolic', 'diastolic', 'pulse'])

/**
 * The one line the screen says about metrics it cannot name: the server's
 * `unregistered` tally as `slug (rows)`, in the order served, or null when
 * there is nothing to say. Registering is Claude's job (`set_vital_metric`),
 * so this is a note and never a form.
 */
export function unregisteredLine(unregistered: Readonly<Record<string, number>>): string | null {
  const parts = Object.entries(unregistered).map(([slug, n]) => `${slug} (${n})`)
  if (parts.length === 0) return null
  return `UNREGISTERED METRICS ARRIVED: ${parts.join(', ')} ✦ NAME THEM IN CLAUDE WITH set_vital_metric`
}

/**
 * Today's systolic/diastolic/pulse rows, one group per `reading_group`.
 * A row with no group stands alone. Newest first, as the list reads.
 */
export function groupBpRows(rows: readonly VitalRow[]): BpGroup[] {
  const groups = new Map<string, BpGroup>()
  for (const row of rows) {
    if (!BP_METRICS.has(row.metric)) continue
    const key = row.reading_group ?? `row:${row.id}`
    let group = groups.get(key)
    if (!group) {
      group = {
        key,
        observed_at: row.observed_at,
        systolic: null,
        diastolic: null,
        pulse: null,
        device: null,
      }
      groups.set(key, group)
    }
    // Any row of the triple may carry the device; the first seen may not.
    group.device ??= row.device_label ?? row.device ?? null
    if (row.metric === 'systolic') group.systolic = row.value
    else if (row.metric === 'diastolic') group.diastolic = row.value
    else if (row.metric === 'pulse') group.pulse = row.value
  }
  return [...groups.values()].sort(
    (a, b) => new Date(b.observed_at).getTime() - new Date(a.observed_at).getTime(),
  )
}

/** `120/80 · 64 bpm · 07:42 · Arm cuff` — with each absent part left out. */
export function bpLine(group: BpGroup): string {
  const parts: string[] = []
  if (group.systolic !== null || group.diastolic !== null) {
    parts.push(`${group.systolic ?? '—'}/${group.diastolic ?? '—'}`)
  }
  if (group.pulse !== null) parts.push(`${group.pulse} bpm`)
  parts.push(clockTime(group.observed_at))
  if (group.device) parts.push(group.device)
  return parts.join(' · ')
}

/* ── Formatting ──────────────────────────────────────────────────────────── */

/** A served figure, at the precision it came in — one decimal at most, as
 *  every number on TRENDS carries. Formatting, not arithmetic. */
function figure(value: number | null | undefined): string {
  if (value === null || value === undefined) return '—'
  return Number.isInteger(value) ? String(value) : value.toFixed(1)
}

/** The mean of a summary block, or `—` for a null block. */
function mean(block: VitalMetricSummary | null): string {
  return figure(block?.mean)
}

/** A positive finite number from a form field, or null if it is blank/unusable. */
function parseField(raw: string): number | null {
  const trimmed = raw.trim()
  if (!trimmed) return null
  const value = Number.parseFloat(trimmed)
  return Number.isFinite(value) && value > 0 ? value : null
}

/* ── Shape of the form ───────────────────────────────────────────────────── */

type Mode = 'glucose' | 'bp'

type BpKey = 'systolic' | 'diastolic' | 'pulse'

const BP_FIELDS: readonly { key: BpKey; label: string; unit: string; placeholder: string }[] = [
  { key: 'systolic', label: 'SYSTOLIC', unit: 'mmHg', placeholder: '120' },
  { key: 'diastolic', label: 'DIASTOLIC', unit: 'mmHg', placeholder: '80' },
  { key: 'pulse', label: 'PULSE', unit: 'bpm', placeholder: '64' },
]

type BpInputs = Record<BpKey, string>

const EMPTY_BP: BpInputs = { systolic: '', diastolic: '', pulse: '' }

/* ── Screen ──────────────────────────────────────────────────────────────── */

export function Vitals() {
  /* The app-shell sync's `epoch` is the refresh signal, as on every other
     screen. FatSecret writes no vitals, but a screen that ignored the epoch
     would be the one tab that does not re-read when the shell says to. Form
     state never keys off it. */
  const { epoch } = useShellSync()

  // Reads.
  const [today, setToday] = useState<Load<VitalRow[]>>({ status: 'loading' })
  const [summary, setSummary] = useState<Load<VitalsSummaryResponse>>({ status: 'loading' })
  const [meals, setMeals] = useState<Load<NutritionEntry[]>>({ status: 'loading' })
  const [devices, setDevices] = useState<Load<VitalDevice[]>>({ status: 'loading' })

  // Form values. Never cleared by anything but a confirmed save.
  const [mode, setMode] = useState<Mode>('glucose')
  const [glucoseIn, setGlucoseIn] = useState('')
  const [bpIn, setBpIn] = useState<BpInputs>(EMPTY_BP)
  const [whenIn, setWhenIn] = useState(() => datetimeLocalValue(new Date()))
  /* Whether the user has edited WHEN since the last save. The field shows "now"
     as a default, but that default is captured when the form is drawn, and a
     tab left open for two hours would otherwise backdate the reading to when it
     was opened. Untouched means "now, at the moment of saving"; touched means
     exactly what was typed. A ref, not state: it changes nothing on screen. */
  const whenTouched = useRef(false)
  const [deviceSlug, setDeviceSlug] = useState<string | null>(null)
  const [newDevice, setNewDevice] = useState('')

  // Writes.
  const [saving, setSaving] = useState(false)
  const [error, setError] = useState<string | null>(null)
  const [saved, setSaved] = useState<string | null>(null)
  const savedTimer = useRef<number | undefined>(undefined)

  /* The in-flight guard, a ref rather than the `saving` state, for the reason
     LOG spells out: state is read from the closure of the render it was created
     in, and two keydowns in one task can both see `false`. A ref flips
     synchronously. The inputs stay enabled throughout — refusing a duplicate
     request is not the same as blocking entry. */
  const inFlight = useRef(false)

  const loadToday = useCallback(async (signal?: AbortSignal) => {
    setToday({ status: 'loading' })
    try {
      // One read serves both the curve and the BP list: a day of every metric
      // sits well inside the server's row cap, and the split is a filter.
      const now = new Date()
      const res = await getVitals(
        { from: localDayStart(now).toISOString(), to: now.toISOString() },
        signal,
      )
      setToday({ status: 'ready', data: res.rows })
    } catch (error) {
      if (isAbort(error)) return
      setToday({ status: 'error', message: errorMessage(error) })
    }
  }, [])

  const loadSummary = useCallback(async (signal?: AbortSignal) => {
    setSummary({ status: 'loading' })
    try {
      const res = await getVitalsSummary({ days: SUMMARY_DAYS }, signal)
      setSummary({ status: 'ready', data: res })
    } catch (error) {
      if (isAbort(error)) return
      setSummary({ status: 'error', message: errorMessage(error) })
    }
  }, [])

  const loadMeals = useCallback(async (signal?: AbortSignal) => {
    setMeals({ status: 'loading' })
    try {
      // Soft: the markers decorate the curve but are not the curve. `days=0`
      // is "since the server's today", the same read LOG's TODAY SO FAR makes.
      const res = await getNutrition({ days: 0 }, signal)
      setMeals({ status: 'ready', data: res.entries })
    } catch (error) {
      if (isAbort(error)) return
      setMeals({ status: 'error', message: errorMessage(error) })
    }
  }, [])

  const loadDevices = useCallback(async (signal?: AbortSignal) => {
    setDevices({ status: 'loading' })
    try {
      // Soft too: without the list the New device field still writes.
      const res = await getVitalDevices(signal)
      setDevices({ status: 'ready', data: res.devices })
    } catch (error) {
      if (isAbort(error)) return
      setDevices({ status: 'error', message: errorMessage(error) })
    }
  }, [])

  useEffect(() => {
    const controller = new AbortController()
    void loadToday(controller.signal)
    void loadSummary(controller.signal)
    void loadMeals(controller.signal)
    void loadDevices(controller.signal)
    return () => controller.abort()
    // `epoch` is the shell sync's refresh signal — see `useShellSync` above.
  }, [loadToday, loadSummary, loadMeals, loadDevices, epoch])

  useEffect(() => () => window.clearTimeout(savedTimer.current), [])

  /* ── Save ──────────────────────────────────────────────────────────────── */

  async function save() {
    if (inFlight.current) return

    // The local `datetime-local` value becomes an ISO instant with this
    // browser's offset — the one conversion the form makes. An untouched field
    // is "now", read at this instant rather than when the form was drawn.
    const when = whenTouched.current ? new Date(whenIn) : new Date()
    if (whenTouched.current && (!whenIn.trim() || Number.isNaN(when.getTime()))) {
      setError('Enter when the reading was taken.')
      return
    }
    // An untouched field still shows the mount-time default; show the instant
    // actually being stored, so the screen and the server agree on it.
    if (!whenTouched.current) setWhenIn(datetimeLocalValue(when))
    const observed_at = when.toISOString()

    // A typed device wins over a chip; a chip over nothing. Omitted entirely
    // when neither is set, so the server stores null rather than "".
    const device = newDevice.trim() || deviceSlug || undefined
    const base = (metric: VitalIn['metric'], value: number, unit: string): VitalIn => ({
      metric,
      value,
      unit,
      observed_at,
      source: 'manual',
      ...(device ? { device } : {}),
    })

    let body: VitalIn | VitalIn[]
    if (mode === 'glucose') {
      const value = parseField(glucoseIn)
      if (value === null) {
        // Say so, and keep what was typed — an unexplained no-op reads as a
        // broken button.
        setError(glucoseIn.trim() ? 'Use a number above zero.' : 'Enter a glucose reading.')
        return
      }
      body = base('glucose', value, 'mg/dL')
    } else {
      // Three rows, one array, one `observed_at`: the server groups them.
      const rows: VitalIn[] = []
      for (const field of BP_FIELDS) {
        const value = parseField(bpIn[field.key])
        if (value === null) {
          setError(
            bpIn[field.key].trim()
              ? `${field.label}: use a number above zero.`
              : 'Enter systolic, diastolic and pulse.',
          )
          return
        }
        rows.push(base(field.key, value, field.unit))
      }
      body = rows
    }

    inFlight.current = true
    setSaving(true)
    setError(null)
    try {
      await postVitals(body)
      // Only now. The numbers clear and the clock resets to now; the device
      // stays chosen, because the next reading is almost always from the same
      // one — a chip selection or a typed name is a setting, not a value.
      setGlucoseIn('')
      setBpIn(EMPTY_BP)
      setWhenIn(datetimeLocalValue(new Date()))
      whenTouched.current = false
      setSaved(mode === 'glucose' ? 'GLUCOSE SAVED' : 'BLOOD PRESSURE SAVED')
      window.clearTimeout(savedTimer.current)
      savedTimer.current = window.setTimeout(() => setSaved(null), SAVED_FLASH_MS)
      void loadToday()
      void loadSummary()
      // A slug the server had not seen may now be a registered device.
      if (newDevice.trim()) {
        // The server keyed the new device on the slug of what was typed and
        // labelled it with that slug; the text the user typed is its real name,
        // so register it as the label. Best-effort: the reading is already saved,
        // and a failure here leaves a device called by its slug, which
        // set_vital_device or a later save can rename.
        const typed = newDevice.trim()
        void (async () => {
          try {
            await postVitalDevice({ slug: typed, label: typed })
          } catch {
            // Leave it named by its slug; nothing typed is lost.
          }
          await loadDevices()
        })()
      }
    } catch (error) {
      // Every typed value stays exactly where it is.
      setError(errorMessage(error))
    } finally {
      inFlight.current = false
      setSaving(false)
    }
  }

  function onEnter(e: KeyboardEvent<HTMLInputElement>) {
    if (e.key === 'Enter') {
      e.preventDefault()
      void save()
    }
  }

  /* ── Derived (geometry and filters only) ───────────────────────────────── */

  const rows = today.status === 'ready' ? today.data : []
  const glucoseRows = rows.filter((r) => r.metric === 'glucose')
  const bpGroups = groupBpRows(rows)
  const ready = summary.status === 'ready' ? summary.data : null
  const range = ready?.range_mgdl ?? null
  const unregistered = unregisteredLine(ready?.unregistered ?? {})
  const curve = buildGlucoseCurve(glucoseRows, range, localDayStart(new Date()))
  const markers = meals.status === 'ready' ? mealMarkers(meals.data) : []
  const deviceList = devices.status === 'ready' ? devices.data : []

  const readError =
    today.status === 'error' ? today.message : summary.status === 'error' ? summary.message : null

  const tile = (value: string): string => (ready ? value : '—')
  const window14 = ready?.window
  const tir = window14?.glucose?.time_in_range_pct
  const bp14 =
    window14 && (window14.systolic || window14.diastolic)
      ? `${mean(window14.systolic)}/${mean(window14.diastolic)}`
      : '—'

  /* ── Render ────────────────────────────────────────────────────────────── */

  return (
    <>
      <div className="milo-heading-row">
        <h2 className="milo-heading">Vitals / {headingDate()}</h2>
      </div>

      {/* Rows arrived under slugs the registry does not know. Said once, here,
          and not acted on: naming a metric is `set_vital_metric` in Claude. */}
      {unregistered ? (
        <div
          className="milo-note"
          role="note"
          aria-label="Unregistered metrics"
          data-testid="unregistered-note"
        >
          {unregistered}
        </div>
      ) : null}

      {readError ? (
        <Alert tone="danger" role="alert" title="Can’t read your vitals.">
          {readError} Entry still works — what you type is kept.
        </Alert>
      ) : null}

      <Window title="GLUCOSE.TODAY" shadow="m">
        {today.status === 'loading' ? (
          <div className="milo-note milo-note--sm">LOADING…</div>
        ) : curve === null ? (
          /* No readings today — drawn as a note, not as an empty axis: a plot
             with no domain has nothing to label and would render as a blank
             frame that looks like a bug rather than an invitation. */
          <div className="milo-note milo-note--sm">
            {today.status === 'error' ? 'CHART UNAVAILABLE' : 'NO GLUCOSE TODAY ✦ LOG ONE BELOW'}
          </div>
        ) : (
          <div className="milo-chart">
            <svg
              className="milo-chart-svg"
              viewBox={`0 0 ${W} ${H}`}
              role="img"
              aria-label={
                `Glucose today: ${glucoseRows.length} readings, last ${figure(curve.last.value)} ${curve.last.unit} at ${clockTime(curve.last.observed_at)}.` +
                (range ? ` Target band ${range[0]} to ${range[1]}.` : '')
              }
            >
              {curve.band ? (
                <rect
                  className="milo-vitals-band"
                  x={PAD_L}
                  width={W - PAD_L - PAD_R}
                  y={curve.band.y}
                  height={curve.band.height}
                />
              ) : null}

              {curve.gridlines.map((line) => (
                <g key={line.label}>
                  <line
                    x1={PAD_L}
                    x2={W - PAD_R}
                    y1={line.y}
                    y2={line.y}
                    stroke="#111111"
                    strokeWidth={1}
                    opacity={0.18}
                  />
                  <text
                    x={4}
                    y={line.labelY}
                    fontFamily="'Geist Mono', monospace"
                    fontSize={11}
                    fontWeight={700}
                    fill="#656565"
                  >
                    {line.label}
                  </text>
                </g>
              ))}

              {HOUR_LABELS.map((hour) => (
                <text
                  key={hour}
                  x={xAtMinutes(hour * 60)}
                  y={H - 8}
                  textAnchor={hour === 0 ? 'start' : hour === 24 ? 'end' : 'middle'}
                  fontFamily="'Geist Mono', monospace"
                  fontSize={10}
                  fontWeight={700}
                  fill="#656565"
                >
                  {twoDigit(hour)}:00
                </text>
              ))}

              {markers.map((marker) => (
                <g key={marker.meal}>
                  <line
                    className="milo-vitals-marker"
                    x1={marker.x}
                    x2={marker.x}
                    y1={PAD_T}
                    y2={H - PAD_B}
                  />
                  <text
                    x={marker.x}
                    y={PAD_T - 6}
                    textAnchor="middle"
                    fontFamily="'Geist Mono', monospace"
                    fontSize={9}
                    fontWeight={800}
                    fill="#FF4DCB"
                  >
                    {marker.label}
                  </text>
                </g>
              ))}

              {curve.dots.map((dot, i) => (
                <circle key={i} cx={dot.x} cy={dot.y} r={2.2} fill="#2F5BFF" opacity={0.55} />
              ))}

              <path
                d={curve.path}
                fill="none"
                stroke="#111111"
                strokeWidth={3}
                strokeLinejoin="round"
                strokeLinecap="round"
              />
            </svg>

            <div className="milo-legend">
              <span>━ GLUCOSE MG/DL</span>
              {range ? (
                <span className="milo-legend-band">
                  ▒ TARGET {range[0]}–{range[1]}
                </span>
              ) : null}
              <span className="milo-legend-goal">╎ MEAL</span>
            </div>
            <div className="milo-note milo-note--sm">
              MEAL MARKERS AT NOMINAL TIMES ✦ BREAKFAST 08:00 · LUNCH 12:30 · SNACK 16:00 · DINNER
              19:00
            </div>
          </div>
        )}
      </Window>

      <Window title="BP.TODAY" shadow="m">
        {today.status === 'loading' ? (
          <div className="milo-note milo-note--sm">LOADING…</div>
        ) : bpGroups.length === 0 ? (
          <div className="milo-note milo-note--sm">
            {today.status === 'error' ? 'LIST UNAVAILABLE' : 'NO BLOOD PRESSURE TODAY ✦ LOG ONE BELOW'}
          </div>
        ) : (
          <div className="milo-rows">
            {bpGroups.map((group) => (
              <div className="milo-row milo-bp-row" key={group.key}>
                {bpLine(group)}
              </div>
            ))}
            <div className="milo-rows-foot">SYS/DIA MMHG ✦ NEWEST FIRST</div>
          </div>
        )}
      </Window>

      {/* Tiles read `—` while loading and while broken alike; the strip above
          makes the distinction once. Every figure here is the server's. */}
      <div className="milo-metrics">
        <Metric color="yellow" label="GLUCOSE 14D · MG/DL" value={tile(mean(window14?.glucose ?? null))} />
        <Metric
          color="mint"
          label="IN RANGE 14D"
          value={tile(tir === undefined || tir === null ? '—' : `${tir}%`)}
        />
        <Metric color="white" label="BP 14D · SYS/DIA" value={tile(bp14)} />
        <Metric color="pink" label="PULSE 14D · BPM" value={tile(mean(window14?.pulse ?? null))} />
      </div>

      <Window title="VITALS.LOG" shadow="m">
        <div className="milo-panel">
          <div className="milo-chips" role="group" aria-label="Reading type">
            <button
              type="button"
              className="milo-chip"
              aria-pressed={mode === 'glucose'}
              onClick={() => setMode('glucose')}
            >
              GLUCOSE
            </button>
            <button
              type="button"
              className="milo-chip"
              aria-pressed={mode === 'bp'}
              onClick={() => setMode('bp')}
            >
              BLOOD PRESSURE
            </button>
          </div>

          {mode === 'glucose' ? (
            <label className="milo-field">
              <span className="milo-field-label">GLUCOSE · MG/DL</span>
              <input
                className="milo-field-input"
                type="number"
                inputMode="decimal"
                step="1"
                min="0"
                placeholder="110"
                aria-label="Glucose in mg/dL"
                value={glucoseIn}
                onChange={(e) => setGlucoseIn(e.target.value)}
                onKeyDown={onEnter}
              />
            </label>
          ) : (
            <div className="milo-field-grid">
              {BP_FIELDS.map((field) => (
                <label className="milo-field" key={field.key}>
                  <span className="milo-field-label">
                    {field.label} · {field.unit.toUpperCase()}
                  </span>
                  <input
                    className="milo-field-input"
                    type="number"
                    inputMode="numeric"
                    step="1"
                    min="0"
                    placeholder={field.placeholder}
                    aria-label={`${field.label.charAt(0)}${field.label.slice(1).toLowerCase()} in ${field.unit}`}
                    value={bpIn[field.key]}
                    onChange={(e) => setBpIn((prev) => ({ ...prev, [field.key]: e.target.value }))}
                    onKeyDown={onEnter}
                  />
                </label>
              ))}
            </div>
          )}

          <label className="milo-field milo-vitals-when">
            <span className="milo-field-label">WHEN</span>
            <input
              className="milo-field-input"
              type="datetime-local"
              aria-label="Observed at"
              value={whenIn}
              onChange={(e) => {
                whenTouched.current = true
                setWhenIn(e.target.value)
              }}
            />
          </label>

          <div className="milo-vitals-device">
            <span className="milo-field-label">DEVICE</span>
            <div className="milo-vitals-device-row">
              {deviceList.length > 0 ? (
                <div className="milo-chips" role="group" aria-label="Device">
                  {deviceList.map((d) => (
                    <button
                      key={d.slug}
                      type="button"
                      className="milo-chip"
                      aria-pressed={deviceSlug === d.slug}
                      onClick={() => {
                        // Choosing a chip is choosing over the typed name.
                        setDeviceSlug(deviceSlug === d.slug ? null : d.slug)
                        setNewDevice('')
                      }}
                    >
                      {d.label}
                    </button>
                  ))}
                </div>
              ) : null}
              <input
                className="milo-field-input milo-vitals-new-device"
                type="text"
                placeholder="New device"
                aria-label="New device"
                value={newDevice}
                onChange={(e) => {
                  // Typing a name deselects the chips: one device per reading.
                  setNewDevice(e.target.value)
                  setDeviceSlug(null)
                }}
              />
            </div>
          </div>

          <Button
            variant="primary"
            iconRight="↗"
            disabled={saving}
            aria-busy={saving}
            onClick={() => void save()}
          >
            Save reading
          </Button>

          {error ? (
            <div className="milo-flash milo-flash--bad" role="alert">
              ✕ {error}
            </div>
          ) : saved ? (
            <div className="milo-flash" role="status">
              ✓ {saved}
            </div>
          ) : null}
        </div>
      </Window>
    </>
  )
}

export default Vitals
